import { Alert, Box, Button, TextInput } from "@mantine/core";
import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { AlertPresets } from "../../mantine/mantinePresets";
import { useToast } from "../../mantine/notifications/useToast";
import { ROUTES } from "../../routes";
import { PasswordResetLayout } from "../Layout/PasswordResetLayout";
import { PublicLayout } from "../Layout/PublicLayout";
import { loginWithCode } from "./utils/loginWithCode";
import { resendOtp } from "./utils/resendOtp";
import { sanitizeRedirectUrl } from "./utils/sanitizeRedirectUrl";

function OtpConfirm() {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState<string>("");
  const [loginError, setLoginError] = useState<string>();
  const [loginInProgress, setLoginInProgress] = useState<boolean>(false);

  const authMethod = searchParams.get("method");
  const userEmail = searchParams.get("email");
  const isEmail2FaMethod = authMethod === "email";

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (!sessionStorage.getItem("ephemeral_token")) {
        location.replace("/login");
      }
    }
  }, []);

  const handleLogin = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoginInProgress(true);

      try {
        await loginWithCode(
          code,
          sessionStorage.getItem("ephemeral_token") || ""
        );

        location.replace(
          sanitizeRedirectUrl(
            searchParams.get("next"),
            ROUTES.productSelectPage
          )
        );
      } catch (_) {
        setLoginError(t("errors.2FA.InvalidOrExpiredCode"));
      } finally {
        setLoginInProgress(false);
      }
    },
    [code, t, searchParams]
  );

  async function handleOtpResend() {
    try {
      await resendOtp(authMethod || "", userEmail || "");
      showToast({
        type: "success",
        message:
          "Der Code wurde erneut gesendet. Bitte überprüfen Sie Ihren E-Mail-Eingang."
      });
    } catch (_) {
      setLoginError(t("error.2FA.CodeSendingFailed"));
    }
  }
  const description = isEmail2FaMethod
    ? `Wir haben Ihnen einen Sicherheitscode zur 2-Faktor-Authentifizierung per E-Mail gesendet. 
         Bitte geben Sie den Code im folgenden Feld ein.
         Alternativ können Sie auch einen Backup-Code eingeben.`
    : `Öffnen Sie Ihre Authentifizierungs-App und geben Sie den für Ihr Konto 
       generierten Sicherheitscode ein.
       Alternativ können Sie auch einen Backup-Code eingeben.`;

  return (
    <PublicLayout>
      <PasswordResetLayout
        description={description}
        title="Zwei-Faktor-Authentifizierung"
      >
        <form className="PasswordResetForm" onSubmit={handleLogin}>
          <Box mb="1rem">
            <TextInput
              label="Authentifizierungscode"
              name="authCode"
              placeholder="Code"
              required
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
            />

            {loginError && (
              <Alert color={AlertPresets.error.color}>{loginError}</Alert>
            )}

            {isEmail2FaMethod && (
              <Button p={0} variant="transparent" onClick={handleOtpResend}>
                Senden Sie den Code erneut
              </Button>
            )}
            <div className="SubmitBtnWrapper">
              <Button
                className="PasswordSubmitBtn"
                color="primary"
                disabled={!code || loginInProgress}
                loading={loginInProgress}
                type="submit"
              >
                Überprüfen
              </Button>
            </div>
          </Box>
        </form>
      </PasswordResetLayout>
    </PublicLayout>
  );
}

export { OtpConfirm };
